var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
/** @jsx jsx */
import { jsx } from 'theme-ui';
import { getFormsByAppointmentTypeIdAction, QuestionType, useApi, } from '@getclarauk/api-client';
import { useEffect, useRef } from 'react';
import { FormGroup } from './FormGroup';
import { Spinner } from './Spinner';
import { SimpleTextField } from './SimpleTextField';
import { date } from '../validators/date';
import { validateEmail } from '../validators/validateEmail';
import { SimpleDateField } from './SimpleDateField';
import { MultilineTextField } from './MultilineTextField';
import { CustomCheckboxField } from './CustomCheckboxField';
import { ChildErrorProvider, useErrorContext } from '../context/ErrorContext';
import { flatMap } from 'lodash';
import { summariseError } from './FormErrorSummary';
export var PatientForms = function (_a) {
    var className = _a.className, appointmentTypeId = _a.appointmentTypeId, value = _a.value, onChange = _a.onChange, fieldsTouched = _a.fieldsTouched, onFieldsTouchedChange = _a.onFieldsTouchedChange;
    var getFormsByAppointmentTypeId = useApi(getFormsByAppointmentTypeIdAction)[0];
    var onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    useEffect(function () {
        getFormsByAppointmentTypeId({ appointmentTypeId: appointmentTypeId }).then(function (forms) {
            onChangeRef.current(forms.map(getFormsValue));
        });
    }, [appointmentTypeId, getFormsByAppointmentTypeId]);
    return value === undefined ? (jsx(Spinner, null)) : value ? (jsx("div", { sx: { mt: 4 }, className: className }, value.map(function (form) {
        return (jsx(ChildErrorProvider, { path: form._id, key: form._id },
            jsx(PatientForm, { value: form, onChange: function (updated) {
                    onChange(value.map(function (x) { return (x === form ? updated : x); }));
                }, fieldsTouched: fieldsTouched && fieldsTouched[form._id], onFieldsTouchedChange: function (touched) {
                    var updated = fieldsTouched || {};
                    updated[form._id] = touched;
                    onFieldsTouchedChange(updated);
                } })));
    }))) : null;
};
var PatientForm = function (_a) {
    var value = _a.value, onChange = _a.onChange, fieldsTouched = _a.fieldsTouched, onFieldsTouchedChange = _a.onFieldsTouchedChange;
    var errors = useErrorContext();
    return (jsx("div", { sx: { mb: 3 } },
        jsx("div", { sx: { color: 'primary', fontWeight: 'bold', fontSize: 3, mb: 3 } }, value.name),
        value.questions.map(function (question, i) {
            var touched = (fieldsTouched === null || fieldsTouched === void 0 ? void 0 : fieldsTouched.length) && fieldsTouched[i];
            var error = touched && (errors === null || errors === void 0 ? void 0 : errors.length) && errors[i];
            return (jsx(FormGroup, { key: i, label: question.text, help: question.helpText, error: error, optional: !question.required },
                jsx(FormQuestion, { value: question, onChange: function (updated) {
                        onChange(__assign(__assign({}, value), { questions: value.questions.map(function (x) {
                                return x === question ? updated : x;
                            }) }));
                    }, error: !!error, onBlur: function () {
                        var updated = fieldsTouched
                            ? __spreadArray([], fieldsTouched, true) : new Array(value.questions.length).fill(false);
                        updated[i] = true;
                        onFieldsTouchedChange(updated);
                    } })));
        })));
};
var FormQuestion = function (_a) {
    var value = _a.value, onChange = _a.onChange, error = _a.error, onBlur = _a.onBlur;
    var fieldProps = {
        value: value.answer,
        onChange: function (answer) {
            onChange(__assign(__assign({}, value), { answer: answer }));
        },
        onBlur: onBlur,
        error: error,
    };
    switch (value.type) {
        case QuestionType.Email:
            return jsx(SimpleTextField, __assign({ type: "email" }, fieldProps));
        case QuestionType.Date:
            return jsx(SimpleDateField, __assign({}, fieldProps));
        case QuestionType.MultiLineText:
            return jsx(MultilineTextField, __assign({}, fieldProps));
        case QuestionType.Checkbox:
            return (jsx(CustomCheckboxField, { value: fieldProps.value === 'yes', onChange: function (answer) {
                    onChange(__assign(__assign({}, value), { answer: answer ? 'yes' : 'no' }));
                    onBlur();
                } }, value.extraText || 'Confirm'));
        default:
            return jsx(SimpleTextField, __assign({}, fieldProps));
    }
};
function getFormsValue(form) {
    return {
        _id: form._id,
        name: form.name,
        questions: form.questions.map(function (question) { return (__assign(__assign({}, question), { answer: '', error: '', touched: false })); }),
    };
}
var questionValidators = (_a = {},
    _a[QuestionType.Email] = function (question) {
        return requiredQuestion(question) || validateEmail(question.answer);
    },
    _a[QuestionType.Date] = function (question) {
        return (requiredQuestion(question) ||
            (date('YYYY-M-D')(question.answer) && "Enter a valid date"));
    },
    _a[QuestionType.Checkbox] = function (question) {
        return question.required && question.answer !== 'yes'
            ? "You must check the box"
            : null;
    },
    _a);
var requiredQuestion = function (question) {
    return question.required && !question.answer ? "Required" : null;
};
export function validatePatientForms(forms) {
    if (!forms) {
        return 'Forms error';
    }
    var errors = {};
    var anyError = false;
    for (var _i = 0, forms_1 = forms; _i < forms_1.length; _i++) {
        var form = forms_1[_i];
        var formErrors = [];
        for (var _a = 0, _b = form.questions; _a < _b.length; _a++) {
            var question = _b[_a];
            var validate = questionValidators[question.type];
            var error = (validate && validate(question)) || requiredQuestion(question);
            formErrors.push(error || '');
            anyError = anyError || !!error;
        }
        errors[form._id] = formErrors;
    }
    return anyError ? errors : null;
}
export function patientFormsGetAllTouched(forms) {
    var touched = {};
    for (var _i = 0, forms_2 = forms; _i < forms_2.length; _i++) {
        var form = forms_2[_i];
        touched[form._id] = new Array(form.questions.length).fill(true);
    }
    return touched;
}
export function patientFormsGetAllErrors(error, formFieldsTouched, forms) {
    return error
        ? flatMap(error, function (questions, formId) {
            return Array.isArray(questions) &&
                questions
                    .filter(function (_, i) {
                    return formFieldsTouched &&
                        formFieldsTouched[formId] &&
                        formFieldsTouched[formId][i];
                })
                    .map(function (error, i) {
                    var form = forms === null || forms === void 0 ? void 0 : forms.find(function (x) { return x._id === formId; });
                    var question = form && form.questions[i];
                    return question ? summariseError(question.text, error) : error;
                });
        })
        : [];
}
//# sourceMappingURL=PatientForms.js.map